/**
 * SpeakerNamer — v2.0.0
 *
 * Post-transcription UI for naming detected speakers.
 * Detects "Speaker 1", "Speaker 2", etc. in the transcript,
 * renders an input per speaker, and replaces all labels when applied.
 *
 * v2.0: Accepts optional `suggestedNames` from visual speaker identification
 * (Gemini Vision scanning video frames). Pre-fills inputs with detected names
 * and shows a "🎥 Visual ID" badge next to each auto-detected name.
 *
 * Shown only when:
 *   - Transcription is complete (AppStep.COMPLETED)
 *   - Transcript contains generic "Speaker N" labels
 */

import { useState, useEffect } from 'react';

interface SpeakerNamerProps {
  transcript: string;
  speakerMap: string;
  /** Names detected from video frames via Gemini Vision. Keys are speaker indices (1-based, matching "Speaker N"). */
  suggestedNames?: Record<number, string>;
  onApply: (updatedTranscript: string, updatedSpeakerMap: string) => void;
}

/** Extract unique "Speaker N" labels from transcript, sorted numerically */
function detectSpeakers(transcript: string): number[] {
  const matches = transcript.matchAll(/\*\*Speaker (\d+)\*\*/g);
  const nums = new Set<number>();
  for (const m of matches) nums.add(parseInt(m[1], 10));
  return Array.from(nums).sort((a, b) => a - b);
}

/** Replace all "Speaker N" occurrences with the given name */
function applyNames(text: string, names: Record<number, string>): string {
  let result = text;
  // Sort descending so "Speaker 10" is replaced before "Speaker 1"
  const nums = Object.keys(names)
    .map(Number)
    .sort((a, b) => b - a);
  for (const n of nums) {
    const name = names[n]?.trim();
    if (!name) continue;
    // Replace **Speaker N** and plain "Speaker N" (in speaker map)
    // Using split/join for ES2020 compatibility (replaceAll requires ES2021)
    result = result.split(`**Speaker ${n}**`).join(`**${name}**`);
    result = result.split(`Speaker ${n}`).join(name);
  }
  return result;
}

export function SpeakerNamer({ transcript, speakerMap, suggestedNames, onApply }: SpeakerNamerProps) {
  const speakerNums = detectSpeakers(transcript);
  const [names, setNames] = useState<Record<number, string>>({});
  const [applied, setApplied] = useState(false);

  // Reset when a new transcript arrives
  useEffect(() => {
    setNames({});
    setApplied(false);
  }, [transcript]);

  // Pre-fill inputs when suggestedNames arrive (may arrive after transcript — async visual ID)
  useEffect(() => {
    if (!suggestedNames || Object.keys(suggestedNames).length === 0) return;
    setNames((prev) => {
      const merged = { ...suggestedNames };
      // User-entered values take priority — don't overwrite what the user typed
      for (const [k, v] of Object.entries(prev)) {
        if (v.trim()) merged[Number(k)] = v;
      }
      return merged;
    });
  }, [suggestedNames]);

  // Don't render if no generic speakers found, or already applied
  if (speakerNums.length === 0 || applied) return null;

  const handleApply = () => {
    const hasAnyName = Object.values(names).some(n => n.trim());
    if (!hasAnyName) return;
    const updatedTranscript = applyNames(transcript, names);
    const updatedSpeakerMap = applyNames(speakerMap, names);
    onApply(updatedTranscript, updatedSpeakerMap);
    setApplied(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleApply();
  };

  const hasVisualID = suggestedNames && Object.keys(suggestedNames).length > 0;

  return (
    <div className="mb-12 sm:mb-20 p-8 sm:p-12 bg-amber-50 rounded-[32px] sm:rounded-[48px] border border-amber-200 border-l-[10px] sm:border-l-[14px] border-l-[#f59e0b] animate-in">
      <div className="flex items-start justify-between gap-4 mb-2">
        <h4 className="text-[10px] sm:text-[11px] font-black text-[#f59e0b] uppercase tracking-[0.4em] sm:tracking-[0.6em]">
          Name Your Speakers
        </h4>
        {hasVisualID && (
          <span className="shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#f59e0b]/10 border border-[#f59e0b]/30 text-[9px] sm:text-[10px] font-black text-[#f59e0b] uppercase tracking-wider">
            🎥 Visual ID Active
          </span>
        )}
      </div>
      <p className="text-[12px] sm:text-[13px] text-gray-500 mb-8 sm:mb-10">
        {hasVisualID
          ? 'Names detected from video frames. Verify and correct if needed, then click Apply.'
          : 'Replace generic labels with real names. Check the first few minutes of the recording to match voices.'}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10">
        {speakerNums.map((n) => {
          const isDetected = !!(suggestedNames?.[n]?.trim());
          return (
            <div key={n} className="flex flex-col gap-1.5">
              <div className="flex items-center gap-3">
                <label className="text-[12px] sm:text-[13px] font-black text-gray-600 whitespace-nowrap w-24 shrink-0">
                  Speaker {n}
                </label>
                {isDetected && (
                  <span className="text-[9px] font-black text-[#f59e0b] uppercase tracking-wider">
                    🎥 Visual ID
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="w-24 shrink-0" />
                <input
                  type="text"
                  placeholder={`e.g. ${n === 1 ? 'Anna Botha' : n === 2 ? 'Sipho Ndlovu' : `Person ${n}`}`}
                  value={names[n] || ''}
                  onChange={(e) => setNames((prev) => ({ ...prev, [n]: e.target.value }))}
                  onKeyDown={handleKeyDown}
                  maxLength={50}
                  className={`flex-1 border rounded-xl px-4 py-3 text-[13px] sm:text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all ${
                    isDetected
                      ? 'bg-amber-50 border-amber-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-300/40'
                      : 'bg-white border-gray-300 focus:border-amber-400 focus:ring-2 focus:ring-amber-200/60'
                  }`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={handleApply}
          disabled={!Object.values(names).some(n => n.trim())}
          className="px-8 py-3 rounded-xl bg-[#f59e0b] text-black text-[12px] sm:text-[13px] font-black uppercase tracking-[0.2em] hover:bg-[#fbbf24] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          Apply Names
        </button>
        <span className="text-[11px] text-gray-400">
          Fill any names you know — blank fields keep their "Speaker N" label. Press Enter to apply.
        </span>
      </div>
    </div>
  );
}
